/**
 * The session type closure (OpenSpec `monorepo-session-data-core` task C2).
 *
 * `SessionResponse` and its hand-authored extension stack moved here from
 * `src/types/index.ts`, and `QuerySessionResponse` — the de-facto cross-domain
 * session contract — out of the hook file that used to declare it
 * (`useQuerySessionById.ts`). Both old paths re-export from here, so type
 * identity is preserved for every existing importer.
 *
 * Everything is moved **as-is**: the extension intersections are deliberate
 * hand-authored overrides of the generated schema (each carries its own
 * TODO about when the schema regen retires it), and a move PR never edits
 * them. `SimpleSessionResponse` and the list-response types rode in with the
 * list family (D1) once their offline-marker seam was decided: they move
 * WHOLE, with `OfflineSyncStatus` re-declared below and parity-pinned.
 */
import {
  type components,
  type operations,
  type SessionStatus,
  type ThumbRating,
} from '@heidi/schema/ml-scribe';

import { type ChunkStatus } from './chunk-status';
import { type RawSessionGroupStatus } from './session-group-status';
import { type SessionMergeGroup } from './session-merge';
import { type StreamingStatus } from './streamingStatus';

// ---------------------------------------------------------------------------
// Consult-note section mappings (INT-4603)
// ---------------------------------------------------------------------------

export type SectionMappingLink = {
  section_heading: string;
  linked_ehr_section_id: string;
};
export type ConsultNoteWithSectionMappings<T> = T & {
  /**
   * INT-4603 markdown section links. This field is returned by newer backend
   * responses before the generated OpenAPI schema exposes it.
   */
  section_mappings?: SectionMappingLink[] | null;
};
type DetailedSessionViewWithSectionMappings = Omit<
  components['schemas']['DetailedSessionView'],
  'consult_note'
> & {
  consult_note: ConsultNoteWithSectionMappings<
    components['schemas']['SimpleConsultNoteView']
  >;
};
type UpdatedDetailedSessionViewWithSectionMappings = Omit<
  components['schemas']['UpdatedDetailedSessionView'],
  'consult_note'
> & {
  consult_note: ConsultNoteWithSectionMappings<
    components['schemas']['SimpleConsultNoteView']
  >;
};

// ---------------------------------------------------------------------------
// Patient link / appointment candidates
// ---------------------------------------------------------------------------

export interface PatientProfileAppointmentFieldMatch {
  profile_attribute: string;
  profile_value: string;
  transcript_support: string;
}

export type PatientProfileAppointmentCandidate =
  | {
      entity_type: 'patient_profile';
      confidence: number;
      patient_profile_id: string;
    }
  | {
      entity_type: 'appointment';
      confidence: number;
      appointment_record_id: string;
    };

export interface PatientProfileAppointmentCandidates {
  extracted_field_matches: PatientProfileAppointmentFieldMatch[];
  ranked_patient_profile_or_appointment: PatientProfileAppointmentCandidate[];
}

export type PatientLinkStatus =
  | 'pending_manual_link'
  | 'auto_linked'
  | 'manually_linked'
  | 'pending_user_review'
  | 'user_reverted_session_merge';

// FE-side wrapper. The schema declares these fields with an inline anonymous
// shape that isn't structurally interchangeable with the typed
// `PatientProfileAppointmentCandidates` consumers expect; the override lets
// downstream code keep using the named type.
export type PatientProfileAppointmentCandidatesExtension = {
  patient_profile_appointment_candidates?: PatientProfileAppointmentCandidates | null;
  patient_link_status?: PatientLinkStatus | null;
};

// ---------------------------------------------------------------------------
// Session detail extensions + SessionResponse
// ---------------------------------------------------------------------------

export type SessionMergeGroupExtension = {
  session_merge_group?: SessionMergeGroup;
};

// Manual extension until the schema is regenerated to expose
// `session_group_status` (APP-7413). Raw wire value — read it through
// `toSessionGroupStatus` so an unknown/missing value degrades to `no_action`.
export type SessionGroupStatusExtension = {
  session_group_status?: RawSessionGroupStatus;
};

// Manual extension until the schema is regenerated to expose
// `transcript_deleted_at`. ISO timestamp stamped by the backend when a
// session's transcript is deleted; drives the "transcript deleted" empty state
// so the FE renders it authoritatively instead of inferring from an empty
// transcript.
export type TranscriptDeletionMarkerExtension = {
  transcript_deleted_at?: string | null;
};

export type SessionResponse = DetailedSessionViewWithSectionMappings &
  PatientProfileAppointmentCandidatesExtension &
  SessionMergeGroupExtension &
  SessionGroupStatusExtension &
  TranscriptDeletionMarkerExtension;
export type SessionUpdateResponse = UpdatedDetailedSessionViewWithSectionMappings;
// The session-feedback submit contract (ARCFE-1001). `FeedbackV2Request`
// carries an FE-ONLY augmentation (`is_safety_issue`) that the generated
// schema does not yet have — preserved verbatim from `@/types`; it drops out
// when `pnpm run schema:dev` picks up MK-1063.
export type FeedbackV2Request = components['schemas']['SubmitFeedbackRequestV2'] & {
  feedback: components['schemas']['FeedbackV2'] & {
    is_safety_issue?: boolean;
  };
};
/**
 * MDC (Medical Device Complaint) classification. HAND-AUTHORED, not a schema
 * pick — the generated `MDCClassification` widens `draft_message` to
 * `string | null | undefined`, and the modal consumer assigns it to a
 * `string | null` field. Moved verbatim from `@/types` (ARCFE-1001); a move
 * PR never edits a type.
 */
export type MDCClassification = {
  is_mdc: boolean;
  draft_message: string | null;
};
export type SubmitFeedbackResponse = {
  success: boolean;
  mdc_classification: MDCClassification | null;
};

/**
 * Problem-based charting (ARCFE-1003). All HAND-AUTHORED, moved verbatim from
 * `@/types` — none of these is a schema pick, so a move PR reproduces them
 * exactly rather than re-deriving from the generated schema.
 */
// Problem-Based Charting Types
export interface CPTCode {
  code: string;
  description: string;
  rationale?: string;
  transcript_excerpt?: string;
}

export interface EvidenceLink {
  source: 'note' | 'transcript' | 'context_tab';
  section?: string | null;
  text: string;
  problem_id: string;
  is_primary: boolean;
  meat_category?: 'monitor' | 'evaluate' | 'assess' | 'treat' | null;
}

export interface SessionProblem {
  problem_id: string;
  name: string;
  ehr_problem_id: string | null;
  snomed_code: string | null;
  icd10_codes: Array<{
    code: string;
    description: string;
    confidence: number;
    rationale?: string;
    transcript_excerpt?: string;
  }>;
  relevance: 'HIGH' | 'MEDIUM' | 'LOW';
  assessment_text?: string;
  plan_text?: string;
  evidence_links?: EvidenceLink[];
  not_in_note?: boolean;
  include_in_assessment?: boolean;
  add_as_diagnosis?: boolean;
  add_to_problem_list?: boolean;
  problem_description?: string;
}

export interface ConfirmProblemRequest {
  problem_id: string;
  include_in_assessment: boolean;
  add_as_diagnosis: boolean;
  add_to_problem_list: boolean;
  /**
   * Provider-selected ICD-10 codes. When absent the backend falls back to the
   * pipeline's suggestions, so a path that knows the resolved selection must
   * send it — the fallback would overwrite a clinician's override.
   */
  icd10_codes?: string[];
}

export interface ConfirmProblemResponse {
  confirmed_count: number;
  success: boolean;
}

export interface SessionProblemsResponse {
  session_id: string;
  problems: SessionProblem[];
  cpt_codes: CPTCode[];
  updated_at: string;
  problems_stale: boolean;
}

/** The documents-list payload (ARCFE-1004). */
export type GetDocumentsResponse = components['schemas']['GetDocumentsResponse'];

/** Context-document metadata update payload (ARCFE-1004). */
export type UpdateSingleSessionContextDocument =
  components['schemas']['UpdateSingleSessionContextDocument'];

/** Session-scoped chat list payload (ARCFE-1003). */
export type ChatListResponse = components['schemas']['ChatListResponse'];

// The PATCH /sessions/:id request body + the transcript-feedback value it
// carries (warm pair, ARCFE-991) — picks matching `@/types`' definitions.
export type SessionUpdate = components['schemas']['SessionUpdate'];
export type Feedback = components['schemas']['Feedback'];

// The PATCH /teams/:t/members/:m/sessions/:s request body, picking
// the same schema symbol `@/types` picks. The app alias stays where it is: it
// is the same generated type, so the two are one definition with two names and
// no importer has to move.
export type UpdateTeamMemberSessionRequest =
  components['schemas']['UpdateTeamMemberSessionRequest'];

// The supported-language vocabulary payload + its element, picking
// the same schema symbols `@/types` picks.
export type SupportedLanguagesResponse = components['schemas']['LanguageView'][];
export type LanguageType = components['schemas']['LanguageView'];

// ---------------------------------------------------------------------------
// The cached session entry (QuerySessionResponse)
// ---------------------------------------------------------------------------

export enum FrontendNoteFailureReason {
  CREATE_DOC_FROM_NOTE_OUT_OF_QUOTA = 'CREATE_DOC_FROM_NOTE_OUT_OF_QUOTA',
  /**
   * Client-classified; never comes from the backend. Set when the consult-note
   * stream fails at the transport level while the client reports no network.
   */
  OFFLINE = 'OFFLINE',
  /**
   * Client-classified; never comes from the backend. Set by the note stream's
   * Pro-Actions recovery when the refused session has no note.
   */
  OUT_OF_PRO_ACTIONS = 'OUT_OF_PRO_ACTIONS',
  /** Client-classified. The consult-note stream request was answered with an untyped 5xx. */
  STREAM_SERVICE_UNAVAILABLE = 'STREAM_SERVICE_UNAVAILABLE',
}

/** Nothing, or a bare editor paragraph (surrounding whitespace ignored). */
export const isConsultNoteEmpty = (result: string | null | undefined) =>
  !result || result.trim() === '<p></p>';

export enum BackupUploadingStatus {
  NOT_STARTED = 'not_started',
  PENDING = 'pending',
  UPLOADING = 'uploading',
  SUCCESS = 'success',
  FAILED = 'failed',
}

/**
 * What actually sits in the React Query cache under `sessionKeys.byId`: the
 * wire payload (`SessionResponse`) decorated with FE-only runtime state the
 * capture/note-generation flows staple onto the entry.
 */
export type QuerySessionResponse = SessionResponse & {
  streamingStatus?: StreamingStatus;
  chunkUploadProgress?: number;
  userDismissedNoSoundDetectedModal?: boolean;
  shouldShowNoteSyncChanges?: boolean;
  shouldShowDocumentSyncChanges?: boolean;
  isSocketError?: boolean;
  backupUploadingStatus?: BackupUploadingStatus;
  // chunk
  chunksUploading?: ChunkStatus[];
  chunksUploadingFinished?: boolean;
  // abort llm stream
  abortController?: AbortController;

  // auto generated note warning
  isDocumentFromNote?: boolean;

  isAutoGeneratedNoteWarningDismissed?: boolean;

  // fe errors
  feError?: FrontendNoteFailureReason;
};

export type SelectQuerySessionResponse<T> = (data: QuerySessionResponse | undefined) => T;

/**
 * The cached document entry — `DocumentView` decorated with the same FE-only
 * generation state (moved from `useQueryDocumentById.ts` to cut the
 * `scribeAPIV2.ts` → hook upward import, task C4).
 */
export type QueryDocumentResponse = components['schemas']['DocumentView'] & {
  streamingStatus?: StreamingStatus;
  // abort llm streaming
  abortController: AbortController;
};

// ---------------------------------------------------------------------------
// Session feedback (moved from `src/types/index.ts` with the D6 family)
// ---------------------------------------------------------------------------

// V2 User Feedback — hand-maintained copies, moved as-is (no mid-move
// cleanup). Verified 2026-07-30: the generated schema NOW defines matching
// `GetUserFeedbackListResponse` / `UserFeedbackView` shapes; aliasing these to
// the generated types is a named Z3 follow-up (deliberate type change, not a
// move).
export type GetUserFeedbackListResponse = {
  /** Feedbacks */
  feedbacks: UserFeedbackView[];
};
export type UserFeedbackView = {
  id: string;
  session_id: string;
  user_id: string;
  target_type: components['schemas']['FeedbackTargetType'];
  target_id?: string | null;
  created_at: string;
  updated_at: string;
  rating: ThumbRating;
  allowed_ratings: ThumbRating[];
};

// ---------------------------------------------------------------------------
// Session list (moved from `src/types/index.ts` + `src/features/sessions/types.ts`
// with the D1 family — the C2 note's deferred seam, now decided: the list
// entry contracts move WHOLE, FE-only offline markers included, exactly like
// `QuerySessionResponse` above)
// ---------------------------------------------------------------------------

/**
 * Re-declared from `src/types/offline-recording.ts` (whose union mirrors the
 * Rust CHECK constraint in `src-tauri/.../schema_v1.sql`) — contracts cannot
 * import app paths. Pinned equal by an app-side parity assertion
 * (`session-key-contract-parity.test.ts`); heals into a cross-package
 * type-only import when offline-recording gets its own package.
 */
export type OfflineSyncStatus =
  | 'recording'
  | 'pending_sync'
  | 'creating_session'
  | 'uploading_audio'
  | 'processing'
  | 'synced'
  | 'sync_failed'
  | 'auth_required'
  | 'upload_failed'
  | 'unrecoverable';

export type OfflineSessionListMarkerExtension = {
  offline_sync_status?: OfflineSyncStatus;
  offline_local_session_id?: string;
  is_offline_synthetic?: boolean;
};
export type SimpleSessionResponse = components['schemas']['SimpleSessionViewV2'] &
  PatientProfileAppointmentCandidatesExtension &
  SessionMergeGroupExtension &
  SessionGroupStatusExtension &
  OfflineSessionListMarkerExtension;
export type SessionsResponse = Omit<
  components['schemas']['SessionsResponse'],
  'sessions'
> & {
  sessions: SimpleSessionResponse[];
};
export type SessionSearchResponse = Omit<
  components['schemas']['SessionSearchResponse'],
  'sessions'
> & {
  sessions: SimpleSessionResponse[];
};
export type SessionStatusCountResponse =
  components['schemas']['SessionStatusCountResponse'];

/**
 * `GET /sessions/status-counts?scope=all` — hand-authored ahead of the schema
 * regen. `Partial` so an unrecognized future status key degrades to "absent"
 * (badge hidden) instead of a type error.
 *
 * TODO: Remove this manual type once the OpenAPI schema is regenerated via
 * `pnpm run schema:dev` and exposes the `/sessions/status-counts` response.
 */
export type SessionStatusCountsResponse = {
  counts: Partial<Record<SessionStatus, number>>;
};
export type SessionSearchQueryParams =
  operations['get_sessions_api_v2_ml_scribe_sessions__get']['parameters']['query'];

// Session Search API types (backend search)
export type SessionSortBy = 'created_at' | 'updated_at' | 'name' | 'status';
export type SessionSearchSortOrder = 'ASC' | 'DESC';

export type SessionSearchParams = {
  q?: string;
  pageSize: number;
  sessionStatus?: SessionStatus[];
  dateFrom?: string;
  dateTo?: string;
  patientProfileIds?: string[];
  untitledText?: string;
  includeTotalCount?: boolean;
  sortBy?: SessionSortBy;
  sortOrder?: SessionSearchSortOrder;
  page: number;
  /**
   * Server-side urgency filter. `true` ⇒ only urgent sessions; omitted ⇒ no
   * urgency filter (unchanged behaviour). See docs/contracts/session-urgency.md.
   */
  isUrgent?: boolean;
};

// The unified session list endpoint (GET /api/v2-2/ml-scribe/sessions).
export type SessionListItem = components['schemas']['SessionListItemView'] &
  PatientProfileAppointmentCandidatesExtension & {
    session_merge_group?: SessionMergeGroup;
    // Raw wire value (un-normalized on the list path) — read through
    // `toSessionGroupStatus` so an unknown value degrades to `no_action` (APP-7413).
    session_group_status?: RawSessionGroupStatus;
    patient_profile_name?: string | null;
    // Frontend-only: the offline recording's sync status, unioned in from
    // `offlineSyncQueueAtom` (APP-6671). Absent on ordinary backend sessions.
    offline_sync_status?: OfflineSyncStatus;
    // Frontend-only: the durable local id for an offline recording row. This
    // remains stable even after `session_id` resolves to a server id mid-sync.
    offline_local_session_id?: string;
    // Frontend-only: true for a synthetic pre-sync row that has no backend session
    // yet (its `session_id` is a local id). Server-only actions must remain
    // blocked, but local-aware navigation and deletion may use this id.
    is_offline_synthetic?: boolean;
    // TODO: Remove once the schema is regenerated — the backend already returns
    // `ehr_appt_id` on list items, but it's not yet in the generated
    // `SessionListItemView`. Drives `isUtc = !ehr_appt_id` timestamp parsing so
    // EHR-appointment times match the session view (naive local) while native
    // sessions stay UTC.
    ehr_appt_id?: string | null;
    // TODO: Remove once the schema is regenerated — the backend returns
    // `session_gist` on list items, but it's not yet in the generated
    // `SessionListItemView`. This is the editable summary the list displays.
    session_gist?: string | null;
  };
export type SessionListResponse = Omit<
  components['schemas']['SessionListResponse'],
  'sessions'
> & {
  sessions: SessionListItem[];
};

// ---------------------------------------------------------------------------
// Session-by-id hub (moved with the D3 family)
// ---------------------------------------------------------------------------

/** Wire shape of `GET /sessions/{id}/permissions` (FGA session permissions). */
export interface SessionPermissionsResponse {
  session_id: string;
  session_permissions: string[];
}

export type BulkCreateSessionParams = components['schemas']['BulkCreateSessionParams'] & {
  /**
   * Optional pre-session notes / "Reason for visit" sent through from the
   * schedule upload preview. Backend trims whitespace; non-blank values are
   * stored on `Session.clinician_notes`. Omitted/empty/whitespace-only values
   * are treated as "no context"; callers MUST omit the field rather than
   * pass `null`.
   *
   * Optional augmentation: field is not yet in the generated OpenAPI schema
   * (moved as-is from `src/types/index.ts` — the regen retires it).
   */
  clinician_notes?: string;
};

/**
 * The offline-local cache marker — session owns its cache-entry contract, so
 * the marker + predicate live here (moved from
 * `src/utils/offline-recording/localSessionSettings.ts`, which re-exports).
 * The offline-recording module STAMPS the marker (`markOfflineLocalSession`
 * stays there); the session hub only ever READS it: an offline-local session
 * exists solely in the RQ cache, so a server fetch would 404 and poison
 * `notFoundSessionIds`.
 *
 * The literal is a persisted-cache-shape contract — frozen-pinned in
 * `types.frozen.test.ts`; renaming it is a deliberate change with a
 * cache-buster note (task Z3).
 */
export const OFFLINE_LOCAL_SESSION_MARKER = '__heidiOfflineLocalSession';

export type OfflineLocalSessionMarker = {
  [OFFLINE_LOCAL_SESSION_MARKER]?: true;
};

export function isOfflineLocalSession(
  session: QuerySessionResponse | undefined
): session is QuerySessionResponse & { [OFFLINE_LOCAL_SESSION_MARKER]: true } {
  return Boolean(
    (session as OfflineLocalSessionMarker | undefined)?.[OFFLINE_LOCAL_SESSION_MARKER]
  );
}

/**
 * Note-suggestion feedback wire shapes. Moved from the `scribeAPIV2.ts` hub
 * (ARCFE-983, with the endpoint carve); the hub re-exports them name-intact.
 */
export type RecordNoteSuggestionFeedbackRequest = {
  feedback: 'accepted' | 'rejected' | 'skipped' | 'skipped_edit';
};

export type RecordNoteSuggestionFeedbackResponse = {
  suggestion_id: string;
  feedback: 'accepted' | 'rejected' | 'skipped' | 'skipped_edit';
  feedback_at: string;
};

/**
 * D5 context-file shapes (ARCFE-990). `SessionContextDocuments` is the
 * schema pick the app's `@/types` barrel already exposes; the view type adds
 * the client-only upload-progress rider the query carries across refetches.
 */
export type SessionContextDocuments = components['schemas']['SessionContextDocumentView'];

export type QuerySessionContextFile = SessionContextDocuments & {
  loadingProgress?: number;
  uploadTrackingId?: string;
};

export type SessionContextFileProgress = {
  name: string;
  progress: number;
  isFinishedGenerating?: boolean;
};

/**
 * One row of the filled-XML document read
 * (`GET /xml-document/{document_id}/fields`).
 *
 * `field_value` is a plain `string`: a field the model left blank comes back
 * empty, not null. Kept under this name rather than the schema's
 * `FilledXmlField` because it is what the readers already import.
 *
 * `section` (the XFA form section heading, null for generic XML),
 * `choice_values` (the values a choice field accepts) and `choice_labels`
 * (display text keyed by value; XFA stores codes such as `adultType1`) are
 * added by hand until the ml-scribe fields ship and `pnpm run schema:dev`
 * regenerates them into `FilledXmlField`; drop the intersection then.
 */
export type XmlDocumentField = components['schemas']['FilledXmlField'] & {
  section?: string | null;
  choice_values?: string[] | null;
  choice_labels?: Record<string, string> | null;
};

/**
 * The sections/rows/cells of a form-DSL XML template.
 *
 * Aliases of the generated `Form*` schemas rather than re-pointed at the picks
 * at each call site, because these are the names the renderers already import.
 * `@heidi/templates/contracts` carries the same aliases — packages may not
 * import each other, but both now resolve to ONE generated declaration.
 */
export type XmlFormOption = components['schemas']['FormOption'];
export type XmlFormCell = components['schemas']['FormCell'];
export type XmlFormRow = components['schemas']['FormRow'];
export type XmlFormSection = components['schemas']['FormSection'];
export type XmlFormLayout = components['schemas']['FormLayout'];

export type XmlFieldUpdate = components['schemas']['XmlFieldUpdate'];

export type UpdateXmlFieldsResponse = components['schemas']['UpdateXmlFieldsResponse'];

/**
 * The filled-XML document read.
 *
 * The endpoint answers **404 until the fill has completed**, so a missing
 * payload means "still filling", not "no such document" — see
 * `useQueryXmlDocumentFields`, which polls through the 404.
 */
export type XmlDocumentFieldsResponse =
  components['schemas']['GetFilledXmlFieldsResponse'];

/**
 * The template format behind an XML-backed document. `xml` reads and writes
 * through `/xml-document`, `xfa_pdf` through `/xfa-document`; the request and
 * response shapes are the same.
 */
export type XmlDocumentFormat = 'xml' | 'xfa_pdf';

// --- moved verbatim from `packages/evidence/src/contracts/answers.ts` -------
// A session is this domain's noun, so the row shape an Evidence answer block
// renders for the `sessions_by_date` tool is declared here.

// Model for a session item in sessions_by_date response
export interface SessionByDateItem {
  session_id: string;
  patient_name?: string;
  patient_demographics?: string;
  session_date: string;
  session_name?: string;
  heidi_output?: string;
  session_status?: string;
  duration_minutes?: number;
}

// --- moved verbatim from `src/types/index.ts` -------------------------------
// The note-styling, document and scheduled-draft picks off session routes.

export type CustomStyle = components['schemas']['CustomStyle'];

export type DocumentView = components['schemas']['DocumentView'];

export type ScheduledSessionDraftView =
  components['schemas']['ScheduledSessionDraftView'];

// The side-by-side consult-note block, moved whole: `SetupResponse` has no
// consumer of its own and rides along because `Candidate` is a pick off it.
export type SideBySideConsultNoteSetupRequest =
  components['schemas']['SideBySideConsultNoteSetupRequest'];
export type SideBySideConsultNoteSetupResponse =
  components['schemas']['SideBySideConsultNoteSetupResponse'];
export type SideBySideConsultNoteGenerationRequest =
  components['schemas']['SideBySideConsultNoteGenerationRequest'];
export type SideBySideConsultNoteConfirmRequest =
  components['schemas']['SideBySideConsultNoteConfirmRequest'];
export type SideBySideConsultNoteConfirmResponse =
  components['schemas']['SideBySideConsultNoteConfirmResponse'];
export type SideBySideConsultNoteCandidate =
  SideBySideConsultNoteSetupResponse['consult_note_list'][0];

// TimelineEventType was removed from the backend OpenAPI schema
// (timeline now uses discriminated unions). Define locally for consumers.
export enum TimelineEventType {
  note_created = 'note_created',
  note_regenerated = 'note_regenerated',
  template_changed = 'template_changed',
  voice_changed = 'voice_changed',
  scribe_changed = 'scribe_changed',
  note_reverted = 'note_reverted',
  edit = 'edit',
}

export type TimelineEvent = components['schemas'][
  | 'NoteCreatedEvent'
  | 'NoteRegeneratedEvent'
  | 'TemplateChangedEvent'
  | 'VoiceChangedEvent'
  | 'ScribeChangedEvent'
  | 'NoteRevertedEvent'
  | 'EditEvent'];

export type UploadContextDocumentsMetaData = {
  name: string;
  date: string;
  additional_context: string;
  unstructured: boolean;
};
